import React from "react";
import moment from "moment";
import { Card } from "reactstrap";

import { endpoint } from "views/pages/manage/common/constant";
import { fetchAllPostPromisedData } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { getDownloadFileUrl } from "views/pages/manage/edpOps/constant/common";
import { masterServiceBaseUrl, enrollmentServiceBaseUrl } from "services/http";
import { fetchAllPromisedData } from "../../common/utils/methods/commonMethods/utilityMethod";
import {
  getInputParamRegex,
  multiValuesSearchPasteHandler,
} from "../../edpOps/constant/student-search";

import { EyeIcon } from "../studentFinancials/paymentCollection/manage-receipts/EyeIcon";
import APopover from "../../common/a-popover";

export const VENDOR_TYPE_ENUM = {
  ADMISSION_ADVISOR: "Admission Advisor",
  INVIGILATOR: "Invigilator",
};

export const vendorTypeOptions = [
  { label: "Admission Advisor", value: "ADMISSION_ADVISOR" },
  { label: "Invigilator", value: "INVIGILATOR" },
];

export const accountStatusOptions = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
  { label: "Initiated", value: "SUBMITTED" },
  { label: "L1 Approved", value: "L1_APPROVED" },
  { label: "L2 Approved", value: "L2_APPROVED" },
  { label: "L3 Approved", value: "L3_APPROVED" },
  { label: "L4 Approved", value: "L4_APPROVED" },
  { label: "L1 Rejected", value: "L1_REJECTED" },
  { label: "L2 Rejected", value: "L2_REJECTED" },
  { label: "L3 Rejected", value: "L3_REJECTED" },
  { label: "L4 Rejected", value: "L4_REJECTED" },
  { label: "Sap Sync Failed", value: "SAP_SYNC_FAILED" },
];

export const paymentSlabStatusOptions = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

export const singleSearchOptions = [
  { label: "SAP Vendor ID", value: "vendorSapCode" },
  { label: "PAN", value: "panNumber" },
  { label: "Vendor Name", value: "vendorName" },
];

export const vendorListSearchByOptions = [
  { value: "By Name", key: "vendorName" },
  { value: "By PAN No.", key: "panNumber" },
  { value: "By SAP Vendor Id", key: "vendorSapCode" },
];

export const paymentHistorySearchByOptions = [
  { value: "By PSID", key: "psid" },
  { value: "By Application ID", key: "applicationId" },
  { value: "By Student Name", key: "studentName" },
];

export const ACTIONABLE_STATUSES = [
  "L1_REJECTED",
  "L2_REJECTED",
  "L3_REJECTED",
  "L4_REJECTED",
  // "SAP_SYNC_FAILED",
];

export const VENDOR_LIST_INITIAL_FILTERS = {
  // searchBy: "vendorName",
  // searchByValue: "",
  vendorType: [],
  businessAreaIds: [],
  status: [],
  onboardedFromDate: null,
  onboardedToDate: null,
  sapPlantId: [],
};

export const PAYMENT_HISTORY_INITIAL_FILTERS = {
  bu: [],
  term: [],
  stream: [],
};

export const formatDateTimeForApi = (value, mode) => {
  if (!value) return "";
  if (mode === "end") return new Date(moment(value).endOf("day")).toISOString();

  return new Date(value).toISOString();
};

export const buildVendorSearchPayload = (filters) => {
  const payload = {
    vendorType: (filters.vendorType || []).map((i) => i?.value ?? i),
    businessAreaIds: (filters.businessAreaIds || []).map((i) => i?.value ?? i),
    status: (filters.status || []).map((i) => i?.value ?? i),
    sapPlantId: (filters.sapPlantId || []).map((i) => i?.value ?? i),
  };

  const onboardedFromDate = formatDateTimeForApi(filters.onboardedFromDate);
  const onboardedToDate = formatDateTimeForApi(filters.onboardedToDate, "end");

  if (onboardedFromDate) payload.onboardedFromDate = onboardedFromDate;
  if (onboardedToDate) payload.onboardedToDate = onboardedToDate;

  const searchBy = filters.searchBy;
  const searchByValue = filters.searchByValue;
  if (searchBy && searchByValue) {
    payload.searchBy = searchBy;
    payload.searchByValue = searchByValue;
  }

  return payload;
};

const buildHistoryPayload = (filters) => {
  const payload = {
    businessAreaIds: (filters.bu || []).map((i) => i?.value ?? i),
    termIds: (filters.term || []).map((i) => i?.value ?? i),
    academicGroupIds: (filters.stream || []).map((i) => i?.value ?? i),
    vendorId: filters.vendorId,
    isPaymentRaised: filters.isPaymentRaised,
  };

  const searchBy = filters.searchBy;
  const searchByValue = filters.searchByValue;
  if (searchBy && searchByValue) {
    payload.searchBy = searchBy;
    payload.searchByValue = searchByValue;
  }

  return payload;
};

export const searchVendors = async ({ filters, offset = 0, limit = 50 }) => {
  const payload = buildVendorSearchPayload(filters || {});
  return await fetchAllPostPromisedData(
    `${endpoint.vendorManagement.search}?offset=${offset}&limit=${limit}`,
    payload,
    "post",
  );
};

export const downloadVendorsCsv = async (filters) => {
  const payload = filters.vendorId
    ? buildHistoryPayload(filters)
    : buildVendorSearchPayload(filters || {});
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorManagement.downloadCsv}`,
    payload,
    "post",
  );

  // expected: response.data => s3 key
  if (response?.code === 200 && response?.data) {
    await getDownloadFileUrl(response.data);
  }

  return response;
};

export const getVendorFilterCount = (filters) => {
  let count = 0;
  const skipKeys = ["searchBy", "searchByValue"]; // searchBy is always present, don't count it

  // Count date range as one filter if either date is set
  if (
    filters?.onboardedFromDate ||
    filters?.onboardedToDate ||
    filters?.approvalDateFrom ||
    filters?.approvalDateTo
  ) {
    count++;
  }

  for (let key in filters) {
    if (
      skipKeys.includes(key) ||
      key === "onboardedFromDate" ||
      key === "onboardedToDate" ||
      key === "approvalDateFrom" ||
      key === "approvalDateTo"
    )
      continue;

    const value = filters[key];

    if (Array.isArray(value)) {
      if (value.length > 0) count++;
    } else if (typeof value === "string") {
      if (value.trim().length > 0) count++;
    } else if (value !== null && value !== undefined) {
      count++;
    }
  }

  return count;
};

export const getVendorDetails = async (id) => {
  return await fetchAllPromisedData(
    `${enrollmentServiceBaseUrl}/vendorData/${id}`,
  );
};

export const getVendorAuditLogs = async (id, limit = 10, offset = 0) => {
  return await fetchAllPromisedData(
    `${endpoint.vendorManagement.auditLogs}?limit=${limit}&offset=${offset}&item_id=${id}&eventType=VENDOR_DETAILS`,
  );
};

// VENDOR PSID MAPPING RELATED API CALLS

export const getVendorPsidMappings = async (vendorId) => {
  return await fetchAllPostPromisedData(
    `${endpoint.vendorPsidMapping.get}/${vendorId}`,
    {},
  );
};

export const searchVendorPsidMapping = async (
  payload,
  offset = 0,
  limit = 50,
) => {
  const request = buildHistoryPayload(payload || {});

  return await fetchAllPostPromisedData(
    `${endpoint.vendorPsidMapping.search}?offset=${offset}&limit=${limit}`,
    request,
  );
};

export const invigilatorPaymentHistory = async (
  payload,
  offset = 0,
  limit = 50,
) => {
  const request = buildHistoryPayload(payload || {});

  return await fetchAllPostPromisedData(
    `${endpoint.raiseVendorRequest.invigilator.search}?offset=${offset}&limit=${limit}`,
    request,
  );
};

export const searchStudentsForPsidMapping = async (
  payload,
  offset = 0,
  limit = 2000,
) => {
  return await fetchAllPostPromisedData(
    `${endpoint.vendorPsidMapping.searchForMapping}?offset=${offset}&limit=${limit}`,
    payload,
  );
};

export const createVendorPsidMapping = async (payload) => {
  return await fetchAllPostPromisedData(
    endpoint.vendorPsidMapping.create,
    payload,
  );
};

export const capitalize = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getValidatedPasteHandler =
  (searchInputParam, setSearchInput) => (e) => {
    multiValuesSearchPasteHandler(e, (sanitizedText) => {
      const regex = getInputParamRegex(searchInputParam);
      const pastedInputs = sanitizedText
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s && regex.test(s));
      const validText = pastedInputs.join(", ");
      setSearchInput((prev) => prev + (prev ? ", " : "") + validText);
    });
  };

// PAYMENT SLAB RELATED API CALLS

export const searchPaymentSlab = async (payload, offset, limit) => {
  // API expects offset (page number) and limit (page size)
  const url = `${endpoint.vendorPaymentSlab.searchPaymentSlab}?offset=${offset}&limit=${limit}`;
  const response = await fetchAllPostPromisedData(url, payload);

  return response;
};

export const fetchSlabDetails = async (slabId) => {
  const response = await fetchAllPromisedData(
    `${endpoint.vendorPaymentSlab.operateSlab}/${slabId}`,
  );

  return response || null;
};

export const createPaymentSlab = async (payload) => {
  const response = await fetchAllPostPromisedData(
    endpoint.vendorPaymentSlab.operateSlab,
    payload,
    "post",
  );
  return response?.data || null;
};

export const updateSlabStatus = async (slabId, status) => {
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorPaymentSlab.operateSlab}/${slabId}/edit`,
    {
      status,
    },
    "put",
  );
  return response;
};

export const updatePaymentSlabRow = async (rowId, payload) => {
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorPaymentSlab.operateSlab}/rows/${rowId}`,
    payload,
    "put",
  );
  return response?.data || null;
};

export const createPaymentSlabRow = async (slabId, payload) => {
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorPaymentSlab.operateSlab}/${slabId}/rows`,
    payload,
    "post",
  );
  return response?.data || null;
};

export const fetchStateAndYearDropdowns = async () => {
  // Fetch state and year dropdowns from master APIs in parallel
  const [stateRes, yearRes] = await Promise.all([
    fetchAllPromisedData(endpoint.state.getAllActive),
    fetchAllPromisedData(endpoint.academic_year.getAllActive),
  ]);

  const stateOptions = stateRes.map((item) => ({
    label: item.stateDispValue,
    value: item.id || item.stateKey,
  }));

  const yearOptions = yearRes.map((item) => ({
    label: item.academicYearDisplayValue,
    value: item.id,
  }));

  return {
    stateOptions,
    yearOptions,
  };
};

export const fetchPaymentSlabDropdowns = async () => {
  // Fetch all required dropdowns in parallel
  const [careerRes, courseAttrRes, courseAttrMappingRes, academicGroupRes] =
    await Promise.all([
      fetchAllPromisedData(endpoint.academic_career.getAllActive),
      fetchAllPromisedData(endpoint.courseAttribute.getAllActive),
      fetchAllPromisedData(
        endpoint.courseAttributeValue.getAllCourseAttributeValue,
      ),
      fetchAllPromisedData(endpoint.academic_group.getAllActive),
    ]);

  const careerOptions = careerRes.map((item) => ({
    label: item.academicCareerDispValue,
    value: item.id,
  }));
  const courseAttributeOptions = courseAttrRes.map((item) => ({
    label: item.courseAttributeDispValue,
    value: item.id,
    desc: item.courseAttributeDesc,
    key: item.courseAttributeKey,
  }));

  const courseAttributeValueMap = courseAttrMappingRes.map((item) => ({
    label: item.courseAttributeValueDispValue,
    value: item.id,
    desc: item.courseAttributeValueDesc,
    key: item.courseAttributeValueKey,
  }));

  const academicGroupOptions = academicGroupRes.map((item) => ({
    label: item.academicGroupDispValue,
    value: item.id,
  }));

  return {
    careerOptions,
    courseAttributeOptions,
    courseAttributeValueMap,
    academicGroupOptions,
  };
};

export const fetchItemTypeOptions = async () => {
  const data = await fetchAllPromisedData(
    `${masterServiceBaseUrl}/itemTypeSetup/getActiveItemTypeSetup`,
  );

  return data.map((it) => ({
    label: `${it.itemTypeId} | ${it.description}`,
    value: it.id,
    key: it.itemTypeId,
  }));
};

export const BUPopover = ({ buId }) => {
  const [businessAreaDesc, setBusinessAreaDesc] = React.useState("");
  const [isFetching, setIsFetching] = React.useState(false);

  const getBuDesc = async () => {
    setIsFetching(true);
    // const businessAreaId = businessAreaOptions?.filter(
    //   (bu) => bu.value === businessArea,
    // )[0]?.businessAreaId;

    const resp = await fetchAllPromisedData(
      `${endpoint?.businessArea.getBusinessAreaByIdV2}/${buId}`,
    );

    if (resp) {
      const description = resp?.businessAreaDesc;
      setBusinessAreaDesc(description);
    } else setBusinessAreaDesc("Unable to show data!");
    setIsFetching(false);
  };

  return (
    <>
      <APopover
        buttonComponent={(onClick, styles) => (
          <span
            onClick={(e) => {
              onClick(e);
              getBuDesc();
            }}
            style={{ cursor: "pointer", marginLeft: "4px" }}
          >
            <EyeIcon />
          </span>
        )}
        menuComponent={(closeForm) => (
          <>
            <Card
              className="mb-0 regular-body label-color"
              style={{ borderRadius: "12px" }}
            >
              <div className="p-2 text-bold">
                {isFetching ? "Loading..." : businessAreaDesc}
              </div>
            </Card>
          </>
        )}
        transformOriginVertical="center"
        transformOriginHorizontal="left"
      />
    </>
  );
};

export const fetchStreamAndTermDropdowns = async () => {
  // Fetch state and year dropdowns from master APIs in parallel
  const [streamRes, termRes] = await Promise.all([
    fetchAllPromisedData(endpoint.academic_group.getAllActive),
    fetchAllPromisedData(endpoint.term.getAllActive),
  ]);

  const streamOptions = streamRes.map((item) => ({
    label: item.academicGroupDispValue,
    value: item.id,
  }));

  const termOptions = termRes.map((item) => ({
    label: item.termDispValue,
    value: item.id,
  }));

  return {
    streamOptions,
    termOptions,
  };
};

export const deletePsidMapping = async (id) => {
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorPsidMapping.delete}/${id}`,
    {},
    "delete",
  );
  return response;
};

export const retrySAPsync = async (id, eventType) => {
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorSapRetry}/${id}?vendorType=${eventType}`,
    {},
    "post",
  );

  return response;
};

export const getLanguageDropdown = async () => {
  const res = await fetchAllPromisedData(`${endpoint?.languageMaster.getAll}`);

  if (res.length) {
    return res;
  }

  return [];
};
